import * as Immutable from 'immutable';
import { createActions } from 'redux-actions';

import { ENDPOINTS_USER } from '../../config/';
import { defaultErrorMessage, globalFetch, isAction } from '../../shared/helpers/fetch-helper';
import { logErrorService } from '../../shared/services';
import { invalidRequest } from '../GenericActions';
import { Permission } from '../../types';

export const {
  performanceResponse,
  performanceRequest,
  performanceResponseFailure,
  profilePerformanceClear,
  performanceActivitiesFailure,
  performanceActivitiesRequest,
  performanceActivitiesResponse,
  performanceActivityFailure,
  performanceActivityRequest,
  performanceActivityResponse,
} = createActions(
  {
    PERFORMANCE_RESPONSE: (data: any) => ({ data }),
    PERFORMANCE_ACTIVITIES_RESPONSE: (data: any) => ({ data }),
    PERFORMANCE_ACTIVITY_RESPONSE: (activity: string, data: any) => ({ activity, data }),
  },
  'PERFORMANCE_REQUEST',
  'PERFORMANCE_RESPONSE_FAILURE',
  'PROFILE_PERFORMANCE_CLEAR',
  'PERFORMANCE_ACTIVITIES_FAILURE',
  'PERFORMANCE_ACTIVITIES_REQUEST',
  'PERFORMANCE_ACTIVITY_FAILURE',
  'PERFORMANCE_ACTIVITY_REQUEST'
);

export function getUserPerformance(type: string, userId: string, permissions: Immutable.List<Permission>) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const isConsultant = permissions.includes('CONSULTANT');

    if (
      state.getIn(['profile', 'performance', 'isfetching']) === true ||
      userId === undefined ||
      // We are allowing WQ employees and researchers accessing Genius Leaderboard,
      // so adding this check to prevent fetching performance incorrectly
      (type === 'genius' && !isConsultant)
    ) {
      return dispatch(invalidRequest());
    }

    dispatch(performanceRequest());

    const endpoint =
      type === 'genius'
        ? `${ENDPOINTS_USER.user}/${userId}/consultant/summary`
        : `${ENDPOINTS_USER.user}/${userId}/${isConsultant ? 'consultant' : 'researcher'}`;

    return globalFetch(
      endpoint,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(performanceResponseFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'PERFORMANCE_RESPONSE_FAILURE')) {
          return json;
        } else {
          return dispatch(performanceResponse(json));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(performanceResponseFailure());
      });
  };
}

export function getPerformanceActivity(activity: string) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (state.getIn(['profile', 'performance', 'activities', 'results', activity]) === undefined || activity === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(performanceActivityRequest(activity));

    const isSuperAlphaEndpoint = activity === 'base-payment';

    return globalFetch(
      `${ENDPOINTS_USER.user}/self/activities/${activity}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(performanceActivityFailure(activity)),
      { ...(isSuperAlphaEndpoint && { endpointVersion: 'version=3.0' }) }
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'PERFORMANCE_ACTIVITY_FAILURE')) {
          return json;
        } else {
          json.isSuperAlpha = isSuperAlphaEndpoint;

          return dispatch(performanceActivityResponse(activity, json));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(performanceActivityFailure(activity));
      });
  };
}

export function getPerformanceActivities() {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    if (state.getIn(['profile', 'performance', 'isfetching']) === true) {
      return dispatch(invalidRequest());
    }
    dispatch(performanceActivitiesRequest());

    return globalFetch(
      `${ENDPOINTS_USER.user}/self/activities`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(performanceActivitiesFailure())
    )
      .then(async (json: any): Promise<object> => {
        let promises = [];
        if (isAction(json, 'PERFORMANCE_ACTIVITIES_FAILURE')) {
          return json;
        } else {
          dispatch(performanceActivitiesResponse(json));
          if (json.results) {
            promises = json.results
              .filter((result: { name: string; title: string }) => result.name !== 'referrals') //todo remove after back end fix
              .map((result: { name: string; title: string }) => dispatch(getPerformanceActivity(result.name)));
          }

          return Promise.all(promises);
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(performanceActivitiesFailure());
      });
  };
}
